import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInWithPopup, signOut, onAuthStateChanged, signInWithEmailAndPassword, sendPasswordResetEmail as firebaseSendPasswordResetEmail, createUserWithEmailAndPassword, updateProfile as updateFirebaseProfile, signInAnonymously as firebaseSignInAnonymously } from 'firebase/auth';
import { initializeFirestore, doc, getDoc, setDoc, updateDoc, deleteDoc, collection, query, where, getDocs, onSnapshot, getDocFromServer, enableIndexedDbPersistence } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, uploadBytesResumable } from 'firebase/storage';
import firebaseConfig from '../firebase-applet-config.json';

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Services
export const auth = getAuth(app);
// Use initializeFirestore with experimentalForceLongPolling: true and useFetchStream: false 
// to fix connectivity issues in proxied/restricted/iframe environments
export const db = initializeFirestore(app, {
  experimentalForceLongPolling: true,
  useFetchStream: false
} as any, firebaseConfig.firestoreDatabaseId);

// Enable local offline persistence to guarantee smooth operations despite backend delays or temporary dropouts
if (typeof window !== 'undefined') {
  enableIndexedDbPersistence(db).catch((err) => {
    console.warn("Firestore offline persistence failed (e.g. multi-tab conflict):", err.code, err.message);
  });
}
export const storage = getStorage(app);
export const googleProvider = new GoogleAuthProvider();

// Auth Helpers
export const signInWithGoogle = () => signInWithPopup(auth, googleProvider);
export const signInAnonymously = () => firebaseSignInAnonymously(auth);
export const logout = () => signOut(auth);
export const loginWithEmail = (email: string, pass: string) => signInWithEmailAndPassword(auth, email, pass);
export const registerWithEmail = (email: string, pass: string) => createUserWithEmailAndPassword(auth, email, pass);
export const sendPasswordResetEmail = (email: string) => firebaseSendPasswordResetEmail(auth, email);
export const updateAuthProfile = (updates: { displayName?: string, photoURL?: string }) => {
  if (auth.currentUser) return updateFirebaseProfile(auth.currentUser, updates);
  return Promise.reject('No user logged in');
};

// Firestore Error Handling
export const OperationType = {
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
  LIST: 'list',
  GET: 'get',
  WRITE: 'write',
  AUTH: 'auth',
} as const;

export type OperationType = typeof OperationType[keyof typeof OperationType];

export interface FirestoreErrorInfo {
  error: string;
  code?: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId: string | undefined;
    email: string | null | undefined;
    emailVerified: boolean | undefined;
    isAnonymous: boolean | undefined;
    tenantId: string | null | undefined;
    providerInfo: {
      providerId: string;
      displayName: string | null;
      email: string | null;
      photoUrl: string | null;
    }[];
  }
}

export function handleFirestoreError(error: any, operationType: OperationType, path: string | null) {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    code: error.code,
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
      providerInfo: auth.currentUser?.providerData.map(provider => ({
        providerId: provider.providerId,
        displayName: provider.displayName,
        email: provider.email,
        photoUrl: provider.photoURL
      })) || []
    },
    operationType,
    path
  }
  console.error(`Firebase ${operationType.toUpperCase()} Error: `, JSON.stringify(errInfo));
  
  if (error.code === 'auth/network-request-failed' || error.message?.includes('network')) {
    console.error("CRITICAL: Network request to Firebase failed. This is often due to browser tracking protection (Brave, Safari, AdBlockers) or strict enterprise firewalls. Please try refreshing or disabling tracking protection for this application.");
    error.message = "NETWORK PROTOCOL FAILURE: SECURITY HANDSHAKE FAILED. Please disable privacy shields or extensions (like Brave Shields or AdBlock) and refresh the page to continue.";
  }

  throw new Error(JSON.stringify(errInfo));
}

// Connection Test
export async function checkDbStatus() {
  try {
    // Try to read common public collection metadata to verify connectivity
    const q = query(collection(db, 'brand_content'), where('key', '==', 'health_check'));
    await getDocs(q);
    return true;
  } catch (error: any) {
    // If it's a permission error, we are CONNECTED but restricted (Rules blocked it)
    if (error.code === 'permission-denied' || error.message?.includes('permission')) return true;
    
    // If it's a not-found error (on a collection? unlikely but for safety)
    if (error.code === 'not-found') return true;

    console.error("Firebase Database check failed:", error.code, error.message);
    
    // Check if it's a known network/offline error
    const isNetworkError = 
      error.code === 'unavailable' || 
      error.code === 'auth/network-request-failed' || 
      error.message?.toLowerCase().includes('network') ||
      error.message?.toLowerCase().includes('handshake');

    return !isNetworkError;
  }
}

async function testConnection() {
  const status = await checkDbStatus();
  if (!status) {
    console.warn("Please check your Firebase configuration. The client is likely offline or blocked.");
  }
}

testConnection();

export { onAuthStateChanged };
